import urls from "../global/constants/UrlConstants";
import { getCallParams, makeCall } from "../utils/service";

const base = urls.url_prod;

export async function listTenantOrganizations() {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/tenant-organizations`, callParams);
}

export async function getTenantOrganization(id: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/tenant-organizations/${id}`, callParams);
}

export async function createTenantOrganization(body: {
  name: string;
  slug: string;
  brandName?: string;
  logoUrl?: string;
  legalName?: string;
  companyType?: string;
  address1?: string;
  address2?: string;
  city?: string;
  zipcode?: string;
  website?: string;
  countryId?: string;
  stateId?: string;
  /** Existing CRM user to make org_admin */
  adminUserId?: string;
  /** Or create a new org admin account */
  adminEmail?: string;
  adminPassword?: string;
  adminName?: string;
  adminPhone?: string;
}) {
  const callParams = await getCallParams("POST", body);
  return makeCall(`${base}/tenant-organizations`, callParams);
}

export async function updateTenantOrganization(
  id: string,
  body: Record<string, any>
) {
  const callParams = await getCallParams("PUT", body);
  return makeCall(`${base}/tenant-organizations/${id}`, callParams);
}

export async function suspendTenantOrganization(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(`${base}/tenant-organizations/${id}/suspend`, callParams);
}

export async function activateTenantOrganization(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(`${base}/tenant-organizations/${id}/activate`, callParams);
}

export async function switchOrganization(organizationId: string) {
  const callParams = await getCallParams("POST", { organizationId });
  return makeCall(`${base}/auth/switch-organization`, callParams);
}

export async function getMyMemberships() {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/organization-memberships/me`, callParams);
}

export async function listOrganizationMemberships(organizationId: string) {
  const callParams = await getCallParams("GET", { organizationId });
  return makeCall(`${base}/organization-memberships`, callParams);
}

export async function removeMembership(membershipId: string) {
  const callParams = await getCallParams("DELETE");
  return makeCall(
    `${base}/organization-memberships/${membershipId}`,
    callParams
  );
}

export async function upsertMembership(body: Record<string, any>) {
  const callParams = await getCallParams("POST", body);
  return makeCall(`${base}/organization-memberships`, callParams);
}

export async function listUserOrganizations(userId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/users/${userId}/organizations`, callParams);
}

export async function syncUserOrganizations(
  userId: string,
  organizationIds: string[],
  roleSlug = "agent"
) {
  const callParams = await getCallParams("PUT", {
    organizationIds,
    roleSlug,
  });
  return makeCall(`${base}/users/${userId}/organizations`, callParams);
}

export async function listOrgIntegrations(organizationId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/integrations`,
    callParams
  );
}

export async function upsertOrgIntegration(
  organizationId: string,
  body: Record<string, any>
) {
  const callParams = await getCallParams("POST", body);
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/integrations`,
    callParams
  );
}

/**
 * Verify an organization's SMTP credentials; sends a test mail when
 * `testTo` is supplied. Masked passwords fall back to the saved secret.
 */
export async function testOrgSmtp(
  organizationId: string,
  body: { credentials: Record<string, any>; testTo?: string }
) {
  // SMTP handshakes can be slow, especially against Office 365.
  const callParams = await getCallParams("POST", body);
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/integrations/smtp/test`,
    callParams,
    60000
  );
}

export async function fetchAuthKeyTemplates(
  organizationId: string,
  channel = "whatsapp"
) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/authkey/templates?channel=${channel}`,
    callParams
  );
}

export async function listStageTemplates(organizationId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/stage-templates`,
    callParams
  );
}

export async function upsertStageTemplate(
  organizationId: string,
  body: Record<string, any>
) {
  const callParams = await getCallParams("PUT", body);
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/stage-templates`,
    callParams
  );
}

export async function deleteStageTemplate(
  organizationId: string,
  templateId: string,
  stageId?: string
) {
  const q = stageId ? `?stageId=${encodeURIComponent(stageId)}` : "";
  const callParams = await getCallParams("DELETE");
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/stage-templates/${templateId}${q}`,
    callParams
  );
}

export async function attachTeamToOrg(organizationId: string, teamId: string) {
  const callParams = await getCallParams("POST", { teamId });
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/teams`,
    callParams
  );
}

export async function listTeamOrganizations(teamId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/teams/${teamId}/organizations`, callParams);
}

export async function listAllOrganizationTeams() {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/organization-teams`, callParams);
}

export async function syncTeamOrganizations(
  teamId: string,
  organizationIds: string[]
) {
  const callParams = await getCallParams("PUT", { organizationIds });
  return makeCall(`${base}/teams/${teamId}/organizations`, callParams);
}

export async function listAuditLogs(query: Record<string, any> = {}) {
  const callParams = await getCallParams("GET", query);
  // makeCall already appends callParams.urlParams — do not pre-bake them into the URL
  return makeCall(`${base}/audit-logs`, callParams);
}

export async function listOrgLeadSources(
  organizationId: string,
  opts: { all?: boolean } = {}
) {
  const callParams = await getCallParams("GET", opts.all ? { all: "1" } : {});
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/lead-sources`,
    callParams
  );
}

export async function upsertOrgLeadSource(
  organizationId: string,
  body: { source_name: string; sourceGroup?: string }
) {
  const callParams = await getCallParams("POST", body);
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/lead-sources`,
    callParams
  );
}

export async function deactivateOrgLeadSource(
  organizationId: string,
  sourceId: string
) {
  const callParams = await getCallParams("DELETE");
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/lead-sources/${sourceId}`,
    callParams
  );
}

export async function fetchAutomationRoutingOptions(
  organizationId: string,
  options?: { lite?: boolean; teamIds?: string[] }
) {
  const callParams = await getCallParams("GET");
  const params = new URLSearchParams();
  if (options?.lite) params.set("lite", "1");
  if (options?.teamIds?.length) params.set("teamIds", options.teamIds.join(","));
  const query = params.toString();
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/automation-routing-options${
      query ? `?${query}` : ""
    }`,
    callParams
  );
}

export async function getCrmLeadPreferences(organizationId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/crm-lead-preferences`,
    callParams
  );
}

export async function updateCrmLeadPreferences(
  organizationId: string,
  body: Record<string, any>
) {
  const callParams = await getCallParams("PUT", body);
  return makeCall(
    `${base}/tenant-organizations/${organizationId}/crm-lead-preferences`,
    callParams
  );
}
